import { Component, OnInit, OnDestroy, ChangeDetectorRef, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { ModuleService } from '../../services/module.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { SetupService } from '../../services/setup.service';
import { EModuleConfigType, NAVIGATION_CONFIG } from './enum/enum-type';
import { EFieldOptionsType } from '../../models/field-options-type.enum';
import { isNumericType, isUnknownType } from './enum/module-config.utils';
import { IModule, ModuleConfigSchema, PathDefinition } from '../../models/setup.interface';
import { PATHS } from './const/paths.const';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CustomQuestionnaireService } from 'src/app/pages/questionarios/services/custom-questionnaire.service';
import { ICustomQuestionnaire } from '../../models/questionnaire.interface';
import { NpsSurveyService } from 'src/app/pages/pesquisa-nps/services/nps-survey.service';
import { INpsSurvey } from 'src/app/pages/pesquisa-nps/models/nps-survey.interface';
import { ModalConfirmacaoService } from 'src/app/shared/services/modal-confirmacao.service';
import { SetupModuleDraftService } from '../../services/setup-module-draft.service';
import { forkJoin } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface ConfigField {
  description: string;
  type: EModuleConfigType;
  optionsType?: string;
  options?: string[];
}

type ConfigSchema = Record<string, ConfigField>;

@Component({
  selector: 'app-setting-module',
  templateUrl: './setting-module.component.html',
  styleUrls: ['./setting-module.component.scss']
})
export class SettingModuleComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private originalConfigModule: any = {};
  ERoleAdminString = ERoleAdminString;
  activeTab: 'configuracoes' | 'comportamento' = 'configuracoes';
  formConfigModule: UntypedFormGroup;
  moduleId = '';
  setupId = '';
  module?: ModuleConfigSchema;
  paths: PathDefinition[] = PATHS;
  modules: IModule[] = [];
  configSchema: ConfigSchema = {};
  configSchemaArray: { key: string; value: ConfigField }[] = [];
  enabledPathsOrdered: { key: string; order: number; name: string; showAsCard?: boolean }[] = [];
  customQuestionnaires: ICustomQuestionnaire[] = [];
  npsSurveys: INpsSurvey[] = [];
  companyId?: string;
  routeBooleanDefaults: Record<string, boolean> = {
    Module_Questionnaire: true
  };

  constructor(
    private readonly modulesService: ModuleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly setupService: SetupService,
    private readonly toastService: ToastService,
    private readonly customQuestionnaireService: CustomQuestionnaireService,
    private readonly npsSurveyService: NpsSurveyService,
    private readonly modalConfirmacaoService: ModalConfirmacaoService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
    // Vem do EditSetupComponent (tela pai). Opcional para a tela continuar funcionando só com
    // o backend se um dia for aberta fora da edição de setup.
    @Optional() private readonly moduleDraft: SetupModuleDraftService | null
  ) {
    this.formConfigModule = new UntypedFormGroup({
      name: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
      requiresAuth: new UntypedFormControl(false),
      requiresTriage: new UntypedFormControl(false)
    });
  }

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.setupId = params['setupId'];
    });
    this.moduleId = this.activatedRoute.snapshot.paramMap.get('moduleId')!;

    this.initializeForm();
  }

  private async initializeForm(): Promise<void> {
    const COPSOQ_MODULE_ID = 'c14bdb58-57b3-4b95-8ce5-3cd8812802df';

    try {
      const [moduleResponse, setupResponse] = await Promise.all([
        this.modulesService.getModuleConfigSchema(this.moduleId).toPromise(),
        this.setupId ? this.setupService.getSetupById(this.setupId).toPromise() : Promise.resolve(null)
      ]);

      if (setupResponse?.data?.companyId) {
        this.companyId = setupResponse.data.companyId;
      }

      if (moduleResponse?.data) {
        this.module = moduleResponse.data;
        const rawConfigSchema = this.module.configSchema || {};
        this.configSchema = Object.entries(rawConfigSchema)
          .filter(([key, value]) => value !== null)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        this.configSchemaArray = Object.entries(this.configSchema)
          .filter(([key, value]) => !Object.values(NAVIGATION_CONFIG).includes(key) && key !== 'enabledPaths')
          .map(([key, value]) => ({ key, value }));

        if (this.hasOptions(EFieldOptionsType.Modules)) {
          this.modulesService
            .getAll()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.modules = response.data;
                const epControl = this.getFormControl('enabledPaths');
                if (epControl) {
                  const cleaned = ((epControl.value || []) as { key: string; order: number }[])
                    .filter((item) => item.key !== 'Module_SkinCare')
                    .filter((item) => this.modules.some((m) => m.route === item.key))
                    .map((item, i) => ({ ...item, order: i + 1 }));
                  epControl.setValue(cleaned);
                }
                this.updateEnabledPathsOrdered();
                this.cdr.markForCheck();
              },
              error: () => {
                this.toastService.toast('Erro ao carregar módulos.', EMessageType.Error);
                this.modules = [];
                this.updateEnabledPathsOrdered();
                this.cdr.markForCheck();
              }
            });
        }

        if (this.hasOptions(EFieldOptionsType.CustomQuestionnaire)) {
          await this.loadCustomQuestionnaires();
        }

        if (this.hasOptions(EFieldOptionsType.NpsSurvey)) {
          await this.loadNpsSurveys();
        }

        if (this.moduleId === COPSOQ_MODULE_ID) {
          const COPSOQ_CATEGORY_ID = '4e45e692-dad7-476d-9c17-59bdacd56b87';
          await this.loadCOPSOQQuestionnaires(COPSOQ_CATEGORY_ID);
        }

        this.getFormControl('name')?.setValue(this.module.name);

        this.addConfigSchemaControls();
      }

      // Módulo recém-adicionado ainda não existe no backend: a configuração vem do rascunho.
      const moduleConfig =
        this.moduleDraft?.get(this.moduleId) ??
        (await this.setupService.getConfigModule(this.setupId, this.moduleId).toPromise())?.data;
      if (moduleConfig) {
        this.originalConfigModule = moduleConfig.configModule || {};
        this.populateFormWithConfigModule(moduleConfig.configModule);
        this.getFormControl('requiresAuth')?.setValue(moduleConfig.requiresAuth ?? false);
        this.getFormControl('requiresTriage')?.setValue(moduleConfig.requiresTriage ?? false);
        if (this.modules.length > 0) {
          this.updateEnabledPathsOrdered();
        }
      }
    } catch {
      this.toastService.toast('Erro ao carregar configurações do módulo.', EMessageType.Error);
    }
  }

  private async loadCustomQuestionnaires(): Promise<void> {
    if (!this.companyId) {
      return;
    }

    try {
      const questionnaires = await this.customQuestionnaireService.getByCompanyId(this.companyId).toPromise();
      this.customQuestionnaires = questionnaires ?? [];
    } catch {
      this.toastService.toast('Erro ao buscar questionários personalizados.', EMessageType.Error);
    }
  }

  private async loadNpsSurveys(): Promise<void> {
    if (!this.companyId) {
      return;
    }

    try {
      const surveys = await this.npsSurveyService.getByCompanyId(this.companyId).toPromise();
      this.npsSurveys = surveys ?? [];
    } catch {
      this.toastService.toast('Erro ao buscar pesquisas NPS.', EMessageType.Error);
      this.npsSurveys = [];
    }
  }

  private async loadCOPSOQQuestionnaires(categoryId: string): Promise<void> {
    if (!this.companyId) {
      return;
    }

    try {
      const questionnaires = await this.customQuestionnaireService.getByCategoryId(categoryId).toPromise();

      this.customQuestionnaires = questionnaires ?? [];
    } catch {
      this.toastService.toast('Erro ao buscar questionários COPSOQ.', EMessageType.Error);
    }
  }

  // Método para adicionar os campos dinamicamente no formulário
  private addConfigSchemaControls(): void {
    const route = this.module!.route;
    const booleanDefault = this.routeBooleanDefaults[route] ?? false;

    Object.entries(this.configSchema).forEach(([key, field]) => {
      const typeInitialValue: Record<EModuleConfigType, any> = {
        [EModuleConfigType.String]: '',
        [EModuleConfigType.Boolean]: booleanDefault,
        [EModuleConfigType.Array]: [],
        [EModuleConfigType.Number]: null,
        [EModuleConfigType.Int]: null,
        [EModuleConfigType.Integer]: null
      };

      let initialValue = typeInitialValue[field.type] ?? '';

      if (field.type === EModuleConfigType.Array && field.options && field.options.length > 0) {
        initialValue = field.options[0];
      }
      const control = new UntypedFormControl(initialValue);

      this.formConfigModule.addControl(key, control);
    });
  }

  // Método para popular o formulário com as configurações recebidas
  private populateFormWithConfigModule(configModule: any): void {
    if (!configModule) {
      return;
    }

    Object.entries(configModule).forEach(([key, value]) => {
      const control = this.getFormControl(key);
      if (control) {
        control.setValue(value);
      }
    });
  }

  saveModuleSettings(): void {
    const formValues = this.formConfigModule.value;

    const emptyVisibleFields = this.configSchemaArray.filter((field) => {
      const value = formValues[field.key];
      return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    });

    if (emptyVisibleFields.length > 0) {
      this.modalConfirmacaoService
        .abrirModalConfirmacao({
          mensagem: 'Existem campos não preenchidos. Deseja salvar mesmo assim?',
          exibeBotaoConfirma: true,
          exibeBotaoCancela: true,
          textoBotaoConfirma: 'Sim',
          textoBotaoCancela: 'Não'
        })
        .subscribe((confirmado) => {
          if (confirmado) {
            this.submitForm();
          }
        });
    } else {
      this.submitForm();
    }
  }

  private submitForm(): void {
    const { requiresAuth, requiresTriage, ...schemaValues } = this.formConfigModule.value;
    const payload = { ...this.originalConfigModule, ...schemaValues };

    // Módulo novo: o toast só confirma que a configuração foi guardada. Que ela ainda depende
    // do "Salvar setup" é dito pelo aviso fixo da edição de setup, para não repetir a mensagem.
    if (this.moduleDraft?.has(this.moduleId)) {
      this.moduleDraft.update(this.moduleId, { configModule: payload, requiresAuth, requiresTriage });
      this.toastService.toast('Configurações do módulo guardadas no rascunho do setup.');
      this.router.navigate(['/dash/setups/editar', this.setupId]).catch(() => {});
      return;
    }

    forkJoin([
      this.setupService.addConfigModule(this.setupId, this.moduleId, payload),
      this.setupService.updateModuleFlags(this.setupId, this.moduleId, { requiresAuth, requiresTriage })
    ]).subscribe(([configResponse, flagsResponse]) => {
      if (configResponse.isSucceed && flagsResponse.isSucceed) {
        this.toastService.toast('Configurações do módulo salvas com sucesso!');
        this.router.navigate(['/dash/setups/editar', this.setupId]).catch(() => {});
      } else {
        this.toastService.toast('Não foi possível salvar as configurações do módulo. Tente novamente.');
      }
    });
  }

  isNumericType = isNumericType;
  isUnknownType = isUnknownType;

  private hasOptions(optionValue: EFieldOptionsType): boolean {
    return Object.values(this.configSchema).some((field: any) => field.optionsType === optionValue);
  }

  onModuleCheckboxChange(event: any, key: string, route: string): void {
    const control = this.getFormControl(key);
    if (!control) return;

    const currentValue: string[] = control.value || [];

    if (event.target.checked) {
      if (!currentValue.includes(route)) {
        control.setValue([...currentValue, route]);
      }
    } else {
      control.setValue(currentValue.filter((r) => r !== route));
      this.toggleModuleEnabledPath(route, false);
    }
  }

  private updateEnabledPathsOrdered(): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) {
      this.enabledPathsOrdered = [];
      return;
    }
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    const cleaned = val
      .filter((item) => item.key !== 'Module_SkinCare')
      .filter((item) => this.modules.some((m) => m.route === item.key))
      .sort((a, b) => a.order - b.order)
      .map((item, i) => ({ key: item.key, order: i + 1, showAsCard: item.showAsCard ?? false }));
    control.setValue(cleaned, { emitEvent: false });
    this.enabledPathsOrdered = cleaned.map((item) => ({
      ...item,
      name: this.modules.find((m) => m.route === item.key)?.name || item.key
    }));
  }

  dropEnabledPaths(event: CdkDragDrop<any[]>): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;
    moveItemInArray(this.enabledPathsOrdered, event.previousIndex, event.currentIndex);
    control.setValue(
      this.enabledPathsOrdered.map((item, i) => ({
        key: item.key,
        order: i + 1,
        showAsCard: item.showAsCard ?? false
      }))
    );
    this.updateEnabledPathsOrdered();
  }

  isModuleEnabledPath(moduleRoute: string): boolean {
    const control = this.getFormControl('enabledPaths');
    if (!control) return false;
    const val: { key: string; order: number }[] = control.value || [];
    return val.some((item) => item.key === moduleRoute);
  }

  toggleModuleEnabledPath(moduleRoute: string, enabled: boolean): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    if (enabled) {
      if (!val.some((item) => item.key === moduleRoute)) {
        control.setValue([...val, { key: moduleRoute, order: val.length + 1, showAsCard: false }]);
      }
    } else {
      const filtered = val.filter((item) => item.key !== moduleRoute);
      control.setValue(filtered.map((item, i) => ({ ...item, order: i + 1 })));
    }
    this.updateEnabledPathsOrdered();
  }

  // Indica se o módulo do fluxo de saúde também deve aparecer como card avulso no Intro.
  isModuleShowAsCard(moduleRoute: string): boolean {
    const control = this.getFormControl('enabledPaths');
    if (!control) return false;
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    return val.find((item) => item.key === moduleRoute)?.showAsCard ?? false;
  }

  // Liga/desliga o flag showAsCard mantendo o módulo dentro do fluxo de saúde.
  toggleModuleShowAsCard(moduleRoute: string, showAsCard: boolean): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    const updated = val.map((item) => (item.key === moduleRoute ? { ...item, showAsCard } : item));
    control.setValue(updated);
    this.updateEnabledPathsOrdered();
  }

  dropPaths(event: CdkDragDrop<any[]>, fieldKey: string): void {
    const control = this.getFormControl(fieldKey);
    if (!control) return;

    const selectedPaths = [...control.value];

    moveItemInArray(selectedPaths, event.previousIndex, event.currentIndex);

    const updatedPaths = selectedPaths.map((path, index) => ({
      ...path,
      order: index + 1
    }));

    control.setValue(updatedPaths);
  }

  onPathCheckboxChange(event: any, fieldKey: string, pathKey: string): void {
    const control = this.getFormControl(fieldKey);
    if (!control) return;

    const currentValue: { key: string; order: number }[] = control.value || [];

    if (event.target.checked) {
      if (!currentValue.some((item) => item.key === pathKey)) {
        control.setValue([...currentValue, { key: pathKey, order: 1 }]);
      }
    } else {
      control.setValue(currentValue.filter((item) => item.key !== pathKey));
    }
  }

  onOrderChange(event: any, pathKey: string): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;

    const order = parseInt(event.target.value, 10) || 0;
    const currentValue: { key: string; order: number }[] = control.value || [];

    const updatedValue = currentValue.map((item) => (item.key === pathKey ? { ...item, order } : item));

    control.setValue(updatedValue);
  }

  isPathSelected(key: string, pathKey: string): boolean {
    const control = this.getFormControl(key);
    if (!control) return false;
    const currentValue: { key: string; order: number }[] = control.value || [];
    return currentValue.some((item) => item.key === pathKey);
  }

  getPathName(pathKey: string): string {
    return this.paths.find((p) => p.key === pathKey)?.name || pathKey;
  }

  getOrder(key: string, pathKey: string): number {
    const control = this.getFormControl(key);
    if (!control) return 0;
    const currentValue: { key: string; order: number }[] = control.value || [];
    const selectedPath = currentValue.find((item) => item.key === pathKey);
    return selectedPath ? selectedPath.order : 0;
  }

  onQuestionnaireCheckboxChange(event: any, fieldKey: string, questionnaireId: string): void {
    const control = this.getFormControl(fieldKey);
    if (!control) return;

    const currentValue: { id: string; order: number }[] = control.value || [];

    if (event.target.checked) {
      if (!currentValue.some((item) => item.id === questionnaireId)) {
        control.setValue([...currentValue, { id: questionnaireId, order: currentValue.length + 1 }]);
      }
    } else {
      control.setValue(currentValue.filter((item) => item.id !== questionnaireId));
    }
  }

  isQuestionnaireSelected(fieldKey: string, questionnaireId: string): boolean {
    const control = this.getFormControl(fieldKey);
    if (!control) return false;
    const currentValue: { id: string; order: number }[] = control.value || [];
    return currentValue.some((item) => item.id === questionnaireId);
  }

  getQuestionnaireName(questionnaireId: string): string {
    return this.customQuestionnaires.find((q) => q.id === questionnaireId)?.name || questionnaireId;
  }

  getControl(fieldKey: string): UntypedFormControl | null {
    return this.getFormControl(fieldKey);
  }

  private getFormControl(fieldKey: string): UntypedFormControl | null {
    if (!fieldKey) {
      return null;
    }

    const control = this.formConfigModule.get(fieldKey) as UntypedFormControl | null;
    if (control) {
      return control;
    }

    const controls = this.formConfigModule.controls as Record<string, UntypedFormControl>;
    return controls[fieldKey] ?? null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
